/**
 * Validation Engine
 * Data-driven validation using language configurations
 * Validates expressions against dynamically loaded language rules
 */

import { parseExpression, extractIdentifiers, extractFunctionCalls, extractOperators } from './expressionParser';
import { getLanguageConfig } from './languageService';
import { C_STANDARD_NAMES, projectFlowParams } from './knownNames';

// ADO #579 — a bracket inside a character or string literal (`c == '('`) is text,
// not syntax: blank out literal contents before counting brackets.
const stripLiterals = (text) => String(text).replace(/'(?:\\.|[^'\\])*'|"(?:\\.|[^"\\])*"/g, (m) => m[0] + ' '.repeat(m.length - 2) + m[0]);



// Cache for language configs to avoid repeated API calls during validation
let configCache = {};

/**
 * Load language configuration (uses cache)
 */
const loadLanguageConfig = async (language) => {
  if (configCache[language]) {
    return configCache[language];
  }
  
  try {
    const config = await getLanguageConfig(language);
    configCache[language] = config;
    return config;
  } catch (error) {
    console.error(`Failed to load language config for ${language}:`, error);
    return null;
  }
};

/**
 * Synchronous version using preloaded config
 */
export const setLanguageConfigCache = (language, config) => {
  configCache[language] = config;
};

/**
 * Clear config cache
 */
export const clearConfigCache = () => {
  configCache = {};
};

/**
 * Validate operators against language rules
 */
const validateOperators = (operators, config) => {
  const errors = [];
  
  if (!config.invalid_operators) return errors;
  
  for (const op of operators) {
    if (config.invalid_operators.includes(op)) {
      errors.push({
        type: 'invalid_operator',
        message: config.invalid_operator_message || `Invalid operator: ${op}`,
        operator: op,
        severity: 'error',
      });
    }
  }
  
  return errors;
};

/**
 * Validate variables against declared resources
 */
const validateVariables = (variables, resources, config) => {
  const errors = [];
  const resourceNames = resources.map(r => r.name);
  const keywords = config.keywords || [];
  
  for (const varName of variables) {
    // Skip if it's a keyword
    if (keywords.includes(varName)) continue;
    
    // Check if it's a known type (like Math, System, console)
    const baseObj = varName.split('.')[0];
    if (['Math', 'System', 'console', 'math'].includes(baseObj)) continue;

    // ADO #246 Phase C — `this` / `self` / `cls` refer to the enclosing
    // instance. The resource-name check can't see them because they're
    // not declared resources, so the validator would flag `this.length`
    // as undeclared. Accept any reference whose base identifier is one
    // of the recognised instance-binding names — covers `this.length`
    // (Java/Python/JS), `self.length` (Python), and `this->length` (C++).
    if (['this', 'self', 'cls'].includes(baseObj)) continue;
    const cppArrowBase = varName.split('->')[0];
    if (['this', 'self'].includes(cppArrowBase)) continue;

    // ADO #579 — a C standard name, or a flow passed as a value (`eachValue(img, negateValue, 0, 0)`).
    if (C_STANDARD_NAMES.has(baseObj) || projectFlowParams(baseObj)) continue;

    // Check if it's declared in resources
    if (!resourceNames.includes(varName) && !resourceNames.includes(baseObj)) {
      errors.push({
        type: 'undeclared_variable',
        message: `Variable "${varName}" is not declared. Create it in the Resources panel first.`,
        variable: varName,
        severity: 'error',
      });
    }
  }
  
  return errors;
};

/**
 * Validate function calls against language function registry
 */
const validateFunctionCalls = (calls, config) => {
  const errors = [];
  const warnings = [];
  
  const functionRegistry = {};
  for (const func of (config.functions || [])) {
    functionRegistry[func.name] = func;
    // Also add lowercase version for case-insensitive matching
    functionRegistry[func.name.toLowerCase()] = func;
  }
  
  for (const call of calls) {
    const funcName = call.name;
    if (!funcName) continue;
    if (funcName === 'sizeof') continue; // ADO #579 — an operator: sizeof(struct Mark), sizeof x
    
    // Look up function in registry
    const funcDef = functionRegistry[funcName] || functionRegistry[funcName.toLowerCase()];
    
    if (funcDef) {
      // Check argument count
      if (call.argCount < funcDef.min_args) {
        errors.push({
          type: 'too_few_arguments',
          message: `Function "${funcName}" requires at least ${funcDef.min_args} argument(s), but got ${call.argCount}`,
          function: funcName,
          expected: funcDef.min_args,
          actual: call.argCount,
          severity: 'error',
        });
      } else if (call.argCount > funcDef.max_args && funcDef.max_args < 99) {
        errors.push({
          type: 'too_many_arguments',
          message: `Function "${funcName}" accepts at most ${funcDef.max_args} argument(s), but got ${call.argCount}`,
          function: funcName,
          expected: funcDef.max_args,
          actual: call.argCount,
          severity: 'error',
        });
      }
      
      // Check for required imports
      if (funcDef.requires_import) {
        warnings.push({
          type: 'requires_import',
          message: `Function "${funcName}" requires: ${funcDef.requires_import}`,
          function: funcName,
          import: funcDef.requires_import,
          severity: 'warning',
        });
      }
      
      // Check for usage notes
      if (funcDef.description && funcDef.description.includes('Use ')) {
        warnings.push({
          type: 'usage_note',
          message: funcDef.description,
          function: funcName,
          severity: 'warning',
        });
      }
    } else if (projectFlowParams(funcName)) {
      // ADO #579 — the student's OWN flow (or an included library's), called in an expression:
      // `isDecimal(number) == 0`. Known by name; its parameter count is the contract.
      const n = projectFlowParams(funcName).length;
      if (call.argCount !== n) {
        errors.push({
          type: call.argCount < n ? 'too_few_arguments' : 'too_many_arguments',
          message: `Flow "${funcName}" takes ${n} value(s), but got ${call.argCount}`,
          function: funcName, expected: n, actual: call.argCount, severity: 'error',
        });
      }
    } else {
      // Unknown function - report as error
      errors.push({
        type: 'unknown_function',
        message: `Unknown function "${funcName}". This function is not recognized in the current language.`,
        function: funcName,
        severity: 'error',
      });
    }
  }
  
  return { errors, warnings };
};

/**
 * Validate syntax patterns in the expression
 */
const validateSyntax = (expression, config) => {
  const errors = [];
  const trimmed = expression.trim();
  
  if (!trimmed) {
    errors.push({
      type: 'empty_expression',
      message: 'Condition expression is required',
      severity: 'error',
    });
    return errors;
  }
  
  // Determine which operators to check based on language
  const isPython = config.id === 'python';
  
  // Check for trailing operators
  const trailingPatterns = isPython
    ? [/\s+(and|or)\s*$/, /[><=!+\-*/%]\s*$/, /[><=!]=?\s*$/]
    : [/&&\s*$/, /\|\|\s*$/, /[><=!+\-*/%]\s*$/, /[><=!]=?\s*$/];
  
  for (const pattern of trailingPatterns) {
    if (pattern.test(trimmed)) {
      errors.push({
        type: 'trailing_operator',
        message: 'Incomplete expression: missing operand after operator',
        severity: 'error',
      });
      break;
    }
  }
  
  // Check for leading operators (excluding unary minus and not)
  const leadingPatterns = isPython
    ? [/^\s*(and|or)\s+/, /^\s*[><=+*/%]/]
    : [/^\s*&&/, /^\s*\|\|/, /^\s*[><=+*/%]/];
  
  for (const pattern of leadingPatterns) {
    if (pattern.test(trimmed)) {
      // Allow unary minus and not/!
      if (!/^\s*-\s*\d/.test(trimmed) && !/^\s*(not|!)\s/.test(trimmed)) {
        errors.push({
          type: 'leading_operator',
          message: 'Invalid expression: unexpected operator at the beginning',
          severity: 'error',
        });
        break;
      }
    }
  }
  
  // Check for unbalanced parentheses
  let parenCount = 0;
  for (const char of stripLiterals(trimmed)) {
    if (char === '(') parenCount++;
    if (char === ')') parenCount--;
    if (parenCount < 0) {
      errors.push({
        type: 'unbalanced_parens',
        message: 'Syntax error: unmatched closing parenthesis',
        severity: 'error',
      });
      break;
    }
  }
  if (parenCount > 0) {
    errors.push({
      type: 'unbalanced_parens',
      message: 'Syntax error: unmatched opening parenthesis',
      severity: 'error',
    });
  }
  
  // Check for empty function calls - but allow zero-argument functions
  // Only flag if there's truly nothing in the parens except maybe whitespace followed by more content
  
  return errors;
};

/**
 * Main validation function - asynchronous version
 * Validates an expression against language rules
 */
export const validateExpression = async (expression, resources = [], language = 'javascript') => {
  const errors = [];
  const warnings = [];
  
  // Load language configuration
  const config = await loadLanguageConfig(language);
  if (!config) {
    errors.push({
      type: 'config_error',
      message: `Failed to load configuration for language: ${language}`,
      severity: 'error',
    });
    return { errors, warnings, valid: false };
  }
  
  // Validate syntax first
  const syntaxErrors = validateSyntax(expression, config);
  errors.push(...syntaxErrors);
  
  // If syntax is completely broken, don't continue
  if (errors.some(e => e.type === 'empty_expression')) {
    return { errors, warnings, valid: false };
  }
  
  // Parse the expression
  const ast = parseExpression(expression);
  
  if (ast.error) {
    errors.push({
      type: 'parse_error',
      message: `Parse error: ${ast.error}`,
      severity: 'error',
    });
  }
  
  // Extract components from AST
  const operators = extractOperators(ast);
  const { variables, functions } = extractIdentifiers(ast);
  const functionCalls = extractFunctionCalls(ast);
  
  // Validate operators
  const operatorErrors = validateOperators(operators, config);
  errors.push(...operatorErrors);
  
  // Validate variables
  const variableErrors = validateVariables(variables, resources, config);
  errors.push(...variableErrors);
  
  // Validate function calls
  const funcValidation = validateFunctionCalls(functionCalls, config);
  errors.push(...funcValidation.errors);
  warnings.push(...funcValidation.warnings);
  
  return {
    errors,
    warnings,
    valid: errors.length === 0,
    ast,
    identifiers: { variables, functions },
  };
};

/**
 * Synchronous validation using cached config
 * Use this for real-time validation in input fields
 */
export const validateExpressionSync = (expression, resources = [], language = 'javascript') => {
  const errors = [];
  const warnings = [];
  
  const config = configCache[language];
  if (!config) {
    // Return minimal validation without language-specific rules
    const syntaxErrors = validateSyntaxBasic(expression);
    errors.push(...syntaxErrors);
    return { errors, warnings, valid: errors.length === 0 };
  }
  
  // Validate syntax
  const syntaxErrors = validateSyntax(expression, config);
  errors.push(...syntaxErrors);
  
  if (errors.some(e => e.type === 'empty_expression')) {
    return { errors, warnings, valid: false };
  }
  
  // Parse and validate
  const ast = parseExpression(expression);
  
  if (ast.error) {
    errors.push({
      type: 'parse_error',
      message: `Parse error: ${ast.error}`,
      severity: 'error',
    });
  }
  
  const operators = extractOperators(ast);
  const { variables, functions } = extractIdentifiers(ast);
  const functionCalls = extractFunctionCalls(ast);
  
  const operatorErrors = validateOperators(operators, config);
  errors.push(...operatorErrors);
  
  const variableErrors = validateVariables(variables, resources, config);
  errors.push(...variableErrors);
  
  const funcValidation = validateFunctionCalls(functionCalls, config);
  errors.push(...funcValidation.errors);
  warnings.push(...funcValidation.warnings);
  
  return {
    errors,
    warnings,
    valid: errors.length === 0,
  };
};

/**
 * Basic syntax validation without language config
 */
const validateSyntaxBasic = (expression) => {
  const errors = [];
  const trimmed = (expression || '').trim();
  
  if (!trimmed) {
    errors.push({
      type: 'empty_expression',
      message: 'Condition expression is required',
      severity: 'error',
    });
    return errors;
  }
  
  // Check parentheses balance
  let parenCount = 0;
  for (const char of stripLiterals(trimmed)) {
    if (char === '(') parenCount++;
    if (char === ')') parenCount--;
    if (parenCount < 0) {
      errors.push({
        type: 'unbalanced_parens',
        message: 'Syntax error: unmatched closing parenthesis',
        severity: 'error',
      });
      break;
    }
  }
  if (parenCount > 0) {
    errors.push({
      type: 'unbalanced_parens',
      message: 'Syntax error: unmatched opening parenthesis',
      severity: 'error',
    });
  }
  
  return errors;
};

/**
 * Preload language config for synchronous validation
 */
export const preloadLanguageConfig = async (language) => {
  await loadLanguageConfig(language);
};

/**
 * Get error messages as strings (for backward compatibility)
 */
export const getErrorMessages = (validationResult) => {
  return validationResult.errors.map(e => e.message);
};

/**
 * Get warning messages as strings
 */
export const getWarningMessages = (validationResult) => {
  return validationResult.warnings.map(w => w.message);
};

export default {
  validateExpression,
  validateExpressionSync,
  preloadLanguageConfig,
  setLanguageConfigCache,
  clearConfigCache,
  getErrorMessages,
  getWarningMessages,
};
